/**
 * RentCast API Integration (via Backend Proxy)
 * 
 * All RentCast API calls go through our backend to keep the API key secure
 */

import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithReauth } from "../api.config";

export const rentcastApi = createApi({
  reducerPath: "rentcastApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["RentCastProperty", "RentCastSearch"],
  endpoints: (build) => ({
    
    // Unified property search
    searchProperties: build.query({
      query: ({
        city,
        state,
        zipCode,
        listingType = 'sale',
        minPrice,
        maxPrice,
        minBeds,
        maxBeds,
        minBaths,
        maxBaths,
        minSqft,
        maxSqft,
        propertyType,
        limit = 50,
        offset = 0,
      } = {}) => {
        const params = new URLSearchParams();
        
        if (city) params.append('city', city);
        if (state) params.append('state', state);
        if (zipCode) params.append('zipCode', zipCode);
        if (listingType) params.append('listingType', listingType);
        if (minPrice) params.append('minPrice', String(minPrice));
        if (maxPrice) params.append('maxPrice', String(maxPrice));
        if (minBeds) params.append('minBeds', String(minBeds));
        if (maxBeds) params.append('maxBeds', String(maxBeds));
        if (minBaths) params.append('minBaths', String(minBaths));
        if (maxBaths) params.append('maxBaths', String(maxBaths));
        if (minSqft) params.append('minSqft', String(minSqft));
        if (maxSqft) params.append('maxSqft', String(maxSqft));
        if (propertyType) params.append('propertyType', propertyType);
        params.append('limit', String(limit));
        params.append('offset', String(offset));
        
        return {
          url: `/rentcast/search?${params.toString()}`,
          method: "GET",
        };
      },
      // Transform to extract properties array from backend response
      transformResponse: (response) => {
        // Backend returns { success: true, data: { properties: [...] } }
        const properties = response?.data?.properties || response?.properties || response?.data || [];
        return { properties, total: response?.data?.total || properties.length };
      },
      providesTags: ["RentCastSearch"],
    }),

    // Search for-sale listings
    searchForSaleListings: build.query({
      query: ({
        city,
        state,
        zipCode,
        minPrice,
        maxPrice,
        minBeds,
        maxBeds,
        minBaths,
        maxBaths,
        minSqft,
        maxSqft,
        propertyType,
        limit = 50,
        offset = 0,
      } = {}) => {
        const params = new URLSearchParams();
        
        if (city) params.append('city', city);
        if (state) params.append('state', state);
        if (zipCode) params.append('zipCode', zipCode);
        if (minPrice) params.append('minPrice', String(minPrice));
        if (maxPrice) params.append('maxPrice', String(maxPrice));
        if (minBeds) params.append('minBeds', String(minBeds));
        if (maxBeds) params.append('maxBeds', String(maxBeds));
        if (minBaths) params.append('minBaths', String(minBaths));
        if (maxBaths) params.append('maxBaths', String(maxBaths));
        if (minSqft) params.append('minSqft', String(minSqft));
        if (maxSqft) params.append('maxSqft', String(maxSqft));
        if (propertyType) params.append('propertyType', propertyType);
        params.append('limit', String(limit));
        params.append('offset', String(offset));
        
        return {
          url: `/rentcast/listings/sale?${params.toString()}`,
          method: "GET",
        };
      },
      providesTags: ["RentCastSearch"],
    }),

    // Search rental listings
    searchRentalListings: build.query({
      query: ({
        city,
        state,
        zipCode,
        minPrice,
        maxPrice,
        minBeds,
        maxBeds,
        minBaths,
        propertyType,
        limit = 50,
        offset = 0,
      } = {}) => {
        const params = new URLSearchParams();
        
        if (city) params.append('city', city);
        if (state) params.append('state', state);
        if (zipCode) params.append('zipCode', zipCode);
        if (minPrice) params.append('minPrice', String(minPrice));
        if (maxPrice) params.append('maxPrice', String(maxPrice));
        if (minBeds) params.append('minBeds', String(minBeds));
        if (maxBeds) params.append('maxBeds', String(maxBeds));
        if (minBaths) params.append('minBaths', String(minBaths));
        if (propertyType) params.append('propertyType', propertyType);
        params.append('limit', String(limit));
        params.append('offset', String(offset));
        
        return {
          url: `/rentcast/listings/rental?${params.toString()}`,
          method: "GET",
        };
      },
      providesTags: ["RentCastSearch"],
    }),

    // Get property by address
    getPropertyByAddress: build.query({
      query: ({ address, city, state, zipCode }) => {
        const params = new URLSearchParams();
        if (address) params.append('address', address);
        if (city) params.append('city', city);
        if (state) params.append('state', state);
        if (zipCode) params.append('zipCode', zipCode);
        
        return {
          url: `/rentcast/property?${params.toString()}`,
          method: "GET",
        };
      },
      providesTags: ["RentCastProperty"],
    }),

    // Get property value estimate
    getPropertyValueEstimate: build.query({
      query: ({ address, city, state, zipCode }) => {
        const params = new URLSearchParams();
        params.append('address', address);
        if (city) params.append('city', city);
        if (state) params.append('state', state);
        if (zipCode) params.append('zipCode', zipCode);
        
        return {
          url: `/rentcast/estimate/value?${params.toString()}`,
          method: "GET",
        };
      },
    }),

    // Get rent estimate
    getRentEstimate: build.query({
      query: ({ address, city, state, zipCode, bedrooms, bathrooms, sqft }) => {
        const params = new URLSearchParams();
        params.append('address', address);
        if (city) params.append('city', city);
        if (state) params.append('state', state);
        if (zipCode) params.append('zipCode', zipCode);
        if (bedrooms) params.append('bedrooms', String(bedrooms));
        if (bathrooms) params.append('bathrooms', String(bathrooms));
        if (sqft) params.append('sqft', String(sqft));
        
        return {
          url: `/rentcast/estimate/rent?${params.toString()}`,
          method: "GET",
        };
      },
    }),
  }),
});

export const {
  useSearchPropertiesQuery,
  useLazySearchPropertiesQuery,
  useSearchForSaleListingsQuery,
  useLazySearchForSaleListingsQuery,
  useSearchRentalListingsQuery,
  useLazySearchRentalListingsQuery,
  useGetPropertyByAddressQuery,
  useLazyGetPropertyByAddressQuery,
  useGetPropertyValueEstimateQuery,
  useLazyGetPropertyValueEstimateQuery,
  useGetRentEstimateQuery,
  useLazyGetRentEstimateQuery,
} = rentcastApi;
